<template>
  <mds-form size="small">
    <mds-layout-grid>
      <mds-row v-if="isProgramBasedMode" :class="$style['form-field-group-row']">
        <mds-col :cols="8">
          <mds-form>
            <mds-combo-box
              v-model="selectedPrograms"
              :placeholder="translate('selectAProgram')"
              :label="translate('assignToProgram')"
              multiple
              size="small"
              :data-set="programOptions"
            ></mds-combo-box> </mds-form
        ></mds-col>
      </mds-row>
      <template v-if="!isMaxFee">
        <mds-row :class="$style['form-field-group-row']">
          <mds-col :cols="5">
            <mds-input
              required
              size="small"
              :label="translate('feeName')"
              :placeholder="translate('namePlaceholder')"
              maxlength="50"
              :error="showNameError"
              :value="schedule.name"
              @input="handleNameChange"
            >
            </mds-input>
          </mds-col>
          <mds-col>
            <mds-fieldset size="small" variation="radio-group" :legend="translate('maxDiscount')">
              <mds-radio-button
                v-for="option in maxDiscountRadioOptions"
                :key="option.value"
                size="small"
                name="maxDiscount"
                :value="option.value"
                :checked="selectedMaxDiscount === option.value"
                @input="handleMaxDiscountChanged(option.value)"
              >
                {{ option.text }}
                <template v-slot:mds-microcopy>
                  <template v-if="option.value === 1">
                    <HypoNumberInput
                      size="small"
                      hidden-label
                      :disabled="!schedule.allowMaxDiscount"
                      :label="translate('discountAllowed')"
                      :placeholder="formatter.number(0, 'shortDecimal')"
                      :is-percent="true"
                      :allow-leave-blank="false"
                      :range="{ min: 0.0001, max: 100 }"
                      :fraction-digits="2"
                      :saved-value="maxDiscount"
                      @update-saved-value="handleMaxDiscountAmountChanged"
                    >
                    </HypoNumberInput>
                  </template>
                </template>
              </mds-radio-button>
            </mds-fieldset>
          </mds-col>
        </mds-row>
        <mds-row :class="$style['form-field-group-row']">
          <mds-col :cols="5">
            <InfoIconField
              :show-info-icons="showInfoIcons"
              show-learn-more
              :labels="moreInfoModalLabels"
              :abbr-content="translate('feeScheduleTypeMsg')"
              :detailed-content="fstDetailedContent"
            >
              <template v-slot:more>
                <mds-section
                  bold
                  border="none"
                  :size="6"
                  :class="$style['learn-more-section']"
                  :title="`${translate('feeSchedule')} - ${translate('tieredFee')}`"
                >
                  {{ translate('tieredFeeMsg') }}
                </mds-section>
                <mds-section
                  bold
                  border="none"
                  :size="6"
                  :class="$style['learn-more-section']"
                  :title="`${translate('feeSchedule')} - ${translate('totalFee')}`"
                >
                  {{ translate('totalFeeMsg') }}
                </mds-section>
              </template>
              <mds-select
                size="small"
                :label="translate('feeScheduleType')"
                :options="feeScheduleTypeOptions"
                :value="schedule.feeScheduleType"
                @change="handleFeeScheduleTypeChanged"
              >
              </mds-select>
            </InfoIconField>
          </mds-col>
          <mds-col :cols="5">
            <mds-fieldset size="small" variation="radio-group" :legend="translate('inputFee')">
              <mds-radio-button
                v-for="item in inputFeeTypeOptions"
                :key="item.value"
                :checked="schedule.inputFeeType === item.value"
                name="inputFeeType"
                size="small"
                :value="item.value"
                @input="handleInputFeeTypeChanged"
              >
                {{ item.text }}
              </mds-radio-button>
            </mds-fieldset>
          </mds-col>
        </mds-row>
        <mds-row>
          <mds-col>
            <mds-button-container>
              <mds-button
                variation="flat"
                icon="plus"
                type="button"
                :disabled="isMaxRowsReached"
                @click="handleRowAdded"
              >
                {{ addRowText }}
              </mds-button>
              <mds-button
                :id="`add-fee-column-${uid}`"
                variation="flat"
                icon="plus"
                type="button"
                :disabled="isMaxColumnsReached || !showBreakdown"
                @click="addFeeColumn"
              >
                {{ translate('addFeeColumn') }}
              </mds-button>
            </mds-button-container>
            <mds-popover
              v-show="showAddFeeColumnPopover"
              v-model="showAddFeeColumnPopover"
              :triggered-by="`add-fee-column-${uid}`"
              :position="['bottom-center']"
              width="200px"
            >
              <mds-form>
                <mds-input
                  v-model="newColumnName"
                  size="small"
                  maxlength="30"
                  :label="translate('feeColumnName')"
                  :placeholder="translate('namePlaceholder')"
                  required
                >
                </mds-input>
                <mds-button-container right-aligned>
                  <mds-button
                    variation="primary"
                    type="button"
                    :disabled="invaildColumnName"
                    @click="handleColumnAdded"
                  >
                    {{ translate('add') }}
                  </mds-button>
                </mds-button-container>
              </mds-form>
            </mds-popover>
          </mds-col>
        </mds-row>
        <mds-row>
          <mds-col>
            <IncrementalTable
              v-if="
                schedule.feeScheduleType === feeScheduleTypesConst.TIERED_FEE_BY_INCREMENTAL_VALUE
              "
              ref="incremental"
              :mwc-id="getMwcIdForChild('incremental-table')"
              :show-breakdown="showBreakdown"
              :prop-fee-schedule-details="schedule.feeScheduleDetails"
              @details-updated="schedule.feeScheduleDetails = $event"
            />
            <BreakpointTable
              v-else-if="
                schedule.feeScheduleType === feeScheduleTypesConst.TIERED_FEE_BY_BREAKPOINT
              "
              ref="breakpoint"
              :mwc-id="getMwcIdForChild('breakpoint-table')"
              :show-breakdown="showBreakdown"
              :fee-schedule-details="schedule.feeScheduleDetails"
              @details-updated="schedule.feeScheduleDetails = $event"
            />
            <TotalFeeTable
              v-else-if="schedule.feeScheduleType === feeScheduleTypesConst.TOTAL_FEE"
              ref="totalFee"
              :mwc-id="getMwcIdForChild('total-fee-table')"
              :show-breakdown="showBreakdown"
              :fee-schedule-details="schedule.feeScheduleDetails"
              @details-updated="schedule.feeScheduleDetails = $event"
            />
          </mds-col>
        </mds-row>
      </template>
      <template v-else>
        <!-- Max Fee -->
        <mds-row :class="$style['form-field-group-row']">
          <mds-col :cols="4">
            <mds-input
              required
              size="small"
              :label="translate('feeName')"
              :placeholder="translate('namePlaceholder')"
              maxlength="50"
              :value="schedule.name"
              :error="validity.name.invalid && validity.name.dirty"
              :error-text="validity.name.errorText"
              @blur="handleMaxFeeNameChanged"
              @keydown.enter="handleMaxFeeNameChanged"
            >
            </mds-input>
          </mds-col>
          <mds-col :cols="4">
            <HypoNumberInput
              required
              size="small"
              :label="translate('inputMaxFee')"
              :placeholder="formatter.number(0, 'shortDecimal')"
              :hidden-label="false"
              :fraction-digits="2"
              :max-fraction-digits="4"
              :saved-value="schedule.maxFee"
              :error="validity.maxFee.invalid && validity.maxFee.dirty"
              :error-text="validity.maxFee.errorText"
              @update-saved-value="handleMaxFeeAmountChanged"
            >
            </HypoNumberInput>
          </mds-col>
        </mds-row>
        <mds-row :class="$style['form-field-group-row']">
          <mds-col :cols="5">
            <mds-fieldset size="small" variation="radio-group" :legend="translate('maxDiscount')">
              <mds-radio-button
                v-for="option in maxDiscountRadioOptions"
                :key="option.value"
                size="small"
                name="maxDiscount"
                :value="option.value"
                :checked="selectedMaxDiscount === option.value"
                @input="handleMaxDiscountChanged(option.value)"
              >
                {{ option.value === 0 ? option.text : '' }}
                <template v-slot:mds-microcopy>
                  <template v-if="option.value === 1">
                    {{ translate('allowAnnualFeeNoLessThan') }}
                    <HypoNumberInput
                      size="small"
                      :disabled="!schedule.allowMaxDiscount"
                      :fraction-digits="2"
                      :saved-value="schedule.annualFeeNoLessThan"
                      :error="noLessThanValidity.invalid && noLessThanValidity.dirty"
                      :error-text="noLessThanValidity.errorText"
                      @update-saved-value="handleAnnualFeeNoLessThanChanged"
                    >
                    </HypoNumberInput>
                    {{ translate('discountAllowed') }}
                    <HypoNumberInput
                      size="small"
                      :disabled="!schedule.allowMaxDiscount"
                      :fraction-digits="2"
                      :saved-value="schedule.maxDiscount"
                      :error="validity.discountUpTo.invalid && validity.discountUpTo.dirty"
                      :error-text="validity.discountUpTo.errorText"
                      @update-saved-value="handleMaxDiscountAmountChanged"
                    >
                    </HypoNumberInput>
                  </template>
                </template>
              </mds-radio-button>
            </mds-fieldset>
          </mds-col>
        </mds-row>
        <mds-row :class="$style['form-field-group-row']">
          <mds-col :cols="10">
            <InfoIconField
              :show-info-icons="showInfoIcons"
              :show-learn-more="false"
              :labels="moreInfoModalLabels"
              :abbr-content="translate('smaMSG')"
            >
              <mds-fieldset size="small" variation="radio-group" :legend="translate('includeSMA')">
                <mds-radio-button
                  v-for="item in includeSMAOptions"
                  :key="item.value"
                  v-model="schedule.separateAccountManagerFee"
                  name="includeSMA"
                  size="small"
                  :value="item.value"
                >
                  {{ item.text }}
                </mds-radio-button>
              </mds-fieldset>
            </InfoIconField>
          </mds-col>
        </mds-row>
      </template>
    </mds-layout-grid>
  </mds-form>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import MdsForm from '@mds/form';
import MdsComboBox from '@mds/combo-box';
import MdsFieldset from '@mds/fieldset';
import MdsInput from '@mds/input';
import MdsSelect from '@mds/select';
import MdsRadioButton from '@mds/radio-button';
import { MdsButton, MdsButtonContainer } from '@mds/button';
import MdsSection from '@mds/section';
import cloneDeep from 'lodash.clonedeep';
import MdsPopover from 'hypo-shared-utils/src/components/ScrollablePopover';
import HypoNumberInput from 'hypo-shared-utils/src/components/HypoNumberInput';
import { Big } from 'hypo-shared-utils';
import InfoIconField from 'hypo-shared-utils/src/components/InfoIconField';
import {
  inputFeeTypeOptions,
  maxDiscountOptions,
  FEE_SCHEDULE_TYPES,
  includeSMAOptions,
} from './_config/constants';
import {
  calculateAnnualAssetBasedFee,
  getDefaultFeeScheduleDetails,
  getDefaultBreakdown,
  isEmptyInput,
} from './utils';
import IncrementalTable from './fees/schedule/IncrementalTable';
import BreakpointTable from './fees/schedule/BreakpointTable';
import TotalFeeTable from './fees/schedule/TotalFeeTable';

let id = 0;

export default {
  name: 'edit-fee-schedule-view',
  components: {
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsForm,
    MdsComboBox,
    MdsFieldset,
    MdsInput,
    MdsSelect,
    MdsRadioButton,
    MdsButtonContainer,
    MdsButton,
    MdsPopover,
    MdsSection,
    IncrementalTable,
    BreakpointTable,
    TotalFeeTable,
    HypoNumberInput,
    InfoIconField,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    columnLimit: {
      type: Number,
      default: 10,
    },
    rowLimit: {
      type: Number,
      default: 20,
    },
    propSchedule: {
      type: Object,
      default: () => {},
    },
    feeScheduleTypes: {
      type: Array,
      default: () => [],
    },
    showInfoIcons: {
      type: Boolean,
      default: true,
    },
    isProgramBasedMode: Boolean,
    programOptions: Array,
  },
  data() {
    return {
      showAddFeeColumnPopover: false,
      newColumnName: '',
      schedule: {},
      validity: {},
      showNameError: false,
    };
  },
  computed: {
    showBreakdown() {
      return this.schedule.inputFeeType === 1;
    },
    inputFeeTypeOptions() {
      return this.transformComponentOptions(inputFeeTypeOptions);
    },
    feeScheduleTypeOptions() {
      return this.transformComponentOptions(this.feeScheduleTypes);
    },
    maxDiscountRadioOptions() {
      return this.transformComponentOptions(maxDiscountOptions);
    },
    includeSMAOptions() {
      return this.transformComponentOptions(includeSMAOptions);
    },
    selectedMaxDiscount() {
      return this.schedule.allowMaxDiscount ? 1 : 0;
    },
    addRowText() {
      const labelKeyMap = {
        0: 'addTier',
        1: 'addTier',
        2: 'addRow',
      };
      const labelKey = labelKeyMap[this.schedule.feeScheduleType] || labelKeyMap[0];
      return this.translate(labelKey);
    },
    isMaxColumnsReached() {
      const { breakdown = [] } = this.schedule.feeScheduleDetails[0];
      return breakdown.length >= this.columnLimit;
    },
    isMaxRowsReached() {
      return this.schedule.feeScheduleDetails.length >= this.rowLimit;
    },
    invaildColumnName() {
      const { breakdown = [] } = this.schedule.feeScheduleDetails[0];
      const newColumnName = this.newColumnName.trim();
      return newColumnName === '' || breakdown.find(i => i.feeName === newColumnName) !== undefined;
    },
    currentTableComponent() {
      const inputFeeTypeAndRefs = { 0: 'incremental', 1: 'breakpoint', 2: 'totalFee' };
      return this.$refs[inputFeeTypeAndRefs[this.schedule.feeScheduleType]];
    },
    isMaxFee() {
      return this.schedule.isMaxFee;
    },
    maxDiscount() {
      return this.schedule.maxDiscount || this.settings.defaultMaxDiscount;
    },
    moreInfoModalLabels() {
      return {
        modalTitle: this.translate('infoModalTitle'),
        learnMore: this.translate('learnMore'),
        close: this.translate('close'),
      };
    },
    fstDetailedContent() {
      const labelKey = 'feeScheduleTypeDetailedMsg';
      if (this.labeller.exists(labelKey) && this.translate(labelKey) !== '') {
        return this.translate(labelKey);
      }
      return '';
    },
    selectedPrograms: {
      get() {
        const result = [];
        if (this.schedule.programList) {
          this.schedule.programList.forEach(programId => {
            if (this.programOptions.find(option => option.value === programId)) {
              result.push(programId);
            }
          });
        }
        return result;
      },
      set(val) {
        this.schedule.programList = val;
      },
    },
    noLessThanValidity() {
      return this.validity.annualFeeNoLessThan;
    },
    feeScheduleTypesConst() {
      return FEE_SCHEDULE_TYPES;
    },
  },
  watch: {
    // for testing purpose
    propSchedule: {
      handler() {
        this.applyPropSchedule();
      },
      deep: true,
    },
  },
  created() {
    this.uid = id;
    id += 1;
    this.applyPropSchedule();
  },
  methods: {
    handleNameChange(event) {
      this.schedule.name = event.trim();
      this.showNameError = this.schedule.name === '';
    },
    defaultFieldValidity() {
      return {
        invalid: false,
        dirty: false,
        errorText: [],
      };
    },
    setDefaultValidityData() {
      this.$set(this.validity, 'name', this.defaultFieldValidity());
      this.$set(this.validity, 'maxFee', this.defaultFieldValidity());
      this.$set(this.validity, 'annualFeeNoLessThan', this.defaultFieldValidity());
      this.$set(this.validity, 'discountUpTo', this.defaultFieldValidity());
    },
    handleMaxFeeNameChanged($event) {
      const maxFeeName = $event.target.value;
      this.schedule.name = maxFeeName;
      this.validateMaxFeeName();
    },
    validateMaxFeeName() {
      const errorText = [];
      const isEmpty = isEmptyInput(this.schedule.name);
      if (isEmpty) {
        errorText.push(this.translate('requiredField'));
      }
      this.$set(this.validity, 'name', {
        invalid: errorText.length > 0,
        dirty: true,
        errorText,
      });
    },
    handleMaxFeeAmountChanged(amount) {
      this.schedule.maxFee = amount;
      this.validateMaxFeeAmount();
      if (this.isMaxFee && this.schedule.allowMaxDiscount) {
        this.validateAnnualFeeNoLessThan();
        this.calculateAndApplyDiscountUpToValue();
        this.validateDiscountUpTo();
      }
    },
    validateMaxFeeAmount() {
      const errorText = [];
      const maxFee = this.schedule.maxFee;
      if (maxFee === 0 || !this.isValidPercentageInput(maxFee)) {
        errorText.push(this.translate('inputPositiveUpToErrMsg'));
      }
      this.$set(this.validity, 'maxFee', {
        invalid: errorText.length > 0,
        dirty: true,
        errorText,
      });
    },
    applyPropSchedule() {
      this.schedule = cloneDeep(this.propSchedule);
      if (this.schedule.isMaxFee) {
        this.setDefaultValidityData();
      }
      this.initBreakdownFees();
    },
    initBreakdownFees() {
      const { isMaxFee, isHistoricMaxFee, inputFeeType, feeScheduleDetails } = this.schedule;
      const { breakdown = [] } = (feeScheduleDetails && feeScheduleDetails[0]) ?? {};
      const isInputFeeScheduleByBreakdown = !isMaxFee && !isHistoricMaxFee && inputFeeType === 1;
      if (isInputFeeScheduleByBreakdown && breakdown.length === 0) {
        this.schedule.feeScheduleDetails.forEach(detailItem => {
          this.$set(detailItem, 'breakdown', getDefaultBreakdown());
        });
      }
    },
    transformComponentOptions(options) {
      return options.map(i => {
        return {
          text: this.translate(i.labelKey),
          value: i.value,
        };
      });
    },
    handleFeeScheduleTypeChanged(value) {
      const feeScheduleType = parseInt(value, 10);
      const feeScheduleDetails = getDefaultFeeScheduleDetails({
        feeScheduleType,
      });
      this.schedule = {
        ...this.schedule,
        feeScheduleType,
        feeScheduleDetails,
      };
    },
    handleInputFeeTypeChanged(inputFeeType) {
      this.schedule.inputFeeType = inputFeeType;
      this.schedule.feeScheduleDetails.forEach(item => {
        item.annualAssetBasedFee = 0;
        if (inputFeeType === 1) {
          item.breakdown = getDefaultBreakdown();
        } else if (item.breakdown && item.breakdown.length > 0) {
          item.breakdown.forEach(i => {
            i.feeAmount = 0;
          });
        }
      });
    },
    handleMaxDiscountChanged(value) {
      const sch = this.schedule;
      sch.allowMaxDiscount = value === 1;
      if (sch.allowMaxDiscount) {
        sch.maxDiscount = this.maxDiscount;
        if (this.isMaxFee) {
          sch.annualFeeNoLessThan =
            sch.annualFeeNoLessThan || this.settings.defaultAnnualFeeNoLessThan;
          this.validateMaxFeeAmount();
          this.validateAnnualFeeNoLessThan();
        }
      } else {
        sch.maxDiscount = null;
        sch.annualFeeNoLessThan = null;
        this.resetAllowMaxDiscountValidity();
      }
    },
    resetAllowMaxDiscountValidity() {
      this.$set(this.validity, 'discountUpTo', this.defaultFieldValidity());
      this.$set(this.validity, 'annualFeeNoLessThan', this.defaultFieldValidity());
    },
    handleMaxDiscountAmountChanged(value) {
      const sch = this.schedule;
      sch.maxDiscount = value;
      this.validateDiscountUpTo();
      if (this.isMaxFee) {
        const discountUpToInvalid =
          this.validity.discountUpTo.invalid && this.validity.discountUpTo.dirty;
        const maxFeeInvalid = this.validity.maxFee.invalid && this.validity.maxFee.dirty;

        if (!discountUpToInvalid && !maxFeeInvalid) {
          const { maxFee, maxDiscount } = sch;
          // maxFee * (1 - maxDiscount / 100)
          sch.annualFeeNoLessThan = Big(-maxDiscount)
            .div(100)
            .plus(1)
            .times(maxFee)
            .round(4)
            .toNumber();
        } else {
          sch.annualFeeNoLessThan = null;
        }
        this.validateAnnualFeeNoLessThan();
      }
    },
    validateDiscountUpTo() {
      const errorText = [];
      if (
        this.schedule.maxDiscount === 0 ||
        !this.isValidPercentageInput(this.schedule.maxDiscount)
      ) {
        errorText.push(this.translate('inputPositiveUpToErrMsg'));
      }
      this.$set(this.validity, 'discountUpTo', {
        invalid: errorText.length > 0,
        dirty: true,
        errorText,
      });
    },
    handleAnnualFeeNoLessThanChanged(value) {
      this.schedule.annualFeeNoLessThan = value;
      this.validateMaxFeeAmount();
      this.validateAnnualFeeNoLessThan();
      this.calculateAndApplyDiscountUpToValue();
      this.validateDiscountUpTo();
    },
    validateAnnualFeeNoLessThan() {
      const errorText = [];
      const noLessThan = this.schedule.annualFeeNoLessThan;
      const maxFee = this.schedule.maxFee;
      const invalidNoLessThan = !this.isValidPercentageInput(noLessThan) || noLessThan >= maxFee;

      if (this.validity.maxFee.invalid) {
        errorText.push(this.translate('maxFeeZeroErrMsg'));
      } else if (invalidNoLessThan) {
        errorText.push(
          this.translate('greaterThanMaxFeeErrMsg', {
            maxFee: this.formatter.number(maxFee, 'shortDecimal'),
          })
        );
      }
      this.$set(this.validity, 'annualFeeNoLessThan', {
        invalid: errorText.length > 0,
        dirty: true,
        errorText,
      });
    },
    isValidPercentageInput(input) {
      return !isEmptyInput(input) && input <= 100 && input >= 0;
    },
    calculateAndApplyDiscountUpToValue() {
      const sch = this.schedule;
      const noLessThanInvalid =
        this.validity.annualFeeNoLessThan?.invalid && this.validity.annualFeeNoLessThan?.dirty;
      const maxFeeInvalid = this.validity.maxFee.invalid && this.validity.maxFee.dirty;

      if (noLessThanInvalid || maxFeeInvalid) {
        sch.maxDiscount = null;
      } else {
        const { maxFee, annualFeeNoLessThan } = sch;
        // ((maxFee - annualFeeNoLessThan) / maxFee) * 100
        sch.maxDiscount = Big(maxFee)
          .minus(annualFeeNoLessThan)
          .div(maxFee)
          .times(100)
          .round(4)
          .toNumber();
      }
    },
    addFeeColumn() {
      this.showAddFeeColumnPopover = true;
    },
    handleRowAdded() {
      const component = this.currentTableComponent;
      if (component && typeof component.addRow === 'function') {
        component.addRow();
      }
    },
    handleColumnAdded() {
      this.showAddFeeColumnPopover = false;
      const component = this.currentTableComponent;
      if (component && typeof component.addFeeColumn === 'function') {
        component.addFeeColumn(this.newColumnName.trim());
      }
      this.newColumnName = '';
    },
    validateInput() {
      const component = this.currentTableComponent;
      if (component && typeof component.validateInput === 'function') {
        return this.$nextTick()
          .then(() => {
            component.validateInput();
          })
          .then(() => {
            this.showNameError = this.schedule.name === '';
            if (this.showNameError) {
              return false;
            }
            const valid = this.schedule.feeScheduleDetails.every(
              item =>
                item.validity &&
                Object.keys(item.validity).filter(field => item.validity[field] === false)
                  .length === 0
            );
            return valid;
          });
      }
      if (this.schedule.isMaxFee) {
        this.validateMaxFeeName();
        this.validateMaxFeeAmount();
        const { name, maxFee, annualFeeNoLessThan, discountUpTo } = this.validity;
        let valid = !name.invalid && !maxFee.invalid;
        if (this.schedule.allowMaxDiscount) {
          this.validateAnnualFeeNoLessThan();
          this.validateDiscountUpTo();
          valid = valid && !annualFeeNoLessThan.invalid && !discountUpTo.invalid;
        }
        return Promise.resolve(valid);
      }
      return Promise.resolve(true);
    },
    processTotalFeeData() {
      const { inputFeeType } = this.schedule;
      let { feeScheduleDetails } = this.schedule;
      feeScheduleDetails = feeScheduleDetails.map(item => {
        const { validity, touched, ...rest } = item;
        let { breakdown = [] } = item;
        if (inputFeeType === 1 && breakdown.length > 0) {
          breakdown = breakdown.map(breakdownItem => {
            return {
              ...breakdownItem,
              feeAmount: breakdownItem.feeAmount || 0,
            };
          });
          const annualAssetBasedFee = calculateAnnualAssetBasedFee(breakdown);
          return {
            ...rest,
            breakdown,
            annualAssetBasedFee,
          };
        }
        return {
          ...rest,
          annualAssetBasedFee: item.annualAssetBasedFee || 0,
        };
      });
      return {
        ...this.schedule,
        feeScheduleDetails,
      };
    },
    emitData() {
      if (!this.isMaxFee) {
        this.schedule.feeScheduleDetails = this.schedule.feeScheduleDetails.filter(
          item => item.lowValue !== null
        );
        if ([1, 2].includes(this.schedule.feeScheduleType)) {
          return this.processTotalFeeData();
        }
      }
      return this.schedule;
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
.mds-layout-grid .mds-layout-grid__row.form-field-group-row {
  margin-bottom: $mds-space-3-x;
  .mds-radio-button__microcopy {
    color: $mds-background-color-dark-gray;
  }
}

.learn-more-section {
  margin-bottom: $mds-space-2-x;
}
</style>
